import React, { useEffect, useRef } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";

import { OverlayPanel } from "primereact/overlaypanel";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import * as QuoteActions from "../Store/Actions/Quote";
import { quoteAnyStatusLabel, quoteAnyStatusSeverity } from "../Constants/quote";
import QuoteDetailModal from "./QuoteDetailModal";

/**
 * QuoteHeaderShortcut — ícone no header (ao lado do câmbio) com acesso rápido às
 * cotações **de hoje** (badge com a contagem) + destaque da cotação ativa.
 * Pensado para acompanhar a cotação que está sendo montada agora.
 *
 * Só renderiza quando logado. Clique numa cotação abre o detalhe (/cotacao/:id).
 */
const QuoteHeaderShortcut = (props) => {
	const { loginStatus, quotes, activeQuoteId, retrieveQuotes, openQuoteDetail, variant = "label" } = props;
	const op = useRef(null);
	const navigate = useNavigate();

	useEffect(() => {
		if (loginStatus) retrieveQuotes().catch(() => {});
	}, [loginStatus, retrieveQuotes]);

	// Cotações recentes (a lista já vem ordenada por updatedAt desc do backend).
	// Atalho é atalho: mostra só as 5 últimas e manda para "ver todas" quando há mais
	// (owner 2026-08-04 — listar 30 aqui não substitui a tela de cotações).
	const RECENTES_LIMITE = 5;
	const total = (quotes || []).length;
	const recentes = (quotes || []).slice(0, RECENTES_LIMITE);
	const isLabel = variant !== "icon"; // "label" (desktop) | "icon" (mobile)

	if (!loginStatus) return null;
	if (!isLabel && total === 0) return null; // no mobile (ícone): só aparece se houver cotação

	const openPanel = (e) => {
		op.current?.toggle(e);
		retrieveQuotes().catch(() => {}); // atualiza ao abrir
	};

	const goToDetail = (id) => {
		op.current?.hide();
		openQuoteDetail(id);
	};

	return (
		<>
			<button
				type="button"
				aria-label="Minhas cotações"
				className="p-button p-component p-button-text headerButton quoteShortcutBtn"
				style={{ display: "inline-flex", alignItems: "center", gap: "0.45rem", position: "relative" }}
				onClick={openPanel}
			>
				{/* Ícone minimalista de cotação: documento + cifrão */}
				<svg viewBox="0 0 24 24" width={isLabel ? 22 : 28} height={isLabel ? 22 : 28} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
					<rect x="5" y="3" width="14" height="18" rx="2.5" />
					<path d="M12 7.2v9.6" />
					<path d="M14.2 9a2.1 2.1 0 0 0-2.2-1.1c-1.2 0-2.1.7-2.1 1.7 0 2.1 4.2 1.2 4.2 3.3 0 1-.9 1.7-2.1 1.7A2.3 2.3 0 0 1 9.8 15" />
				</svg>
				{isLabel && <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Minhas cotações</span>}
				{total > 0 && (
					<span className="p-badge p-badge-info" style={isLabel ? { fontSize: "0.65rem" } : { position: "absolute", top: "0", right: "-2px", fontSize: "0.65rem" }}>
						{total}
					</span>
				)}
			</button>
			<OverlayPanel ref={op} style={{ width: "min(360px, 92vw)" }}>
				<div style={{ padding: "0.85rem" }}>
				<div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
					{total > RECENTES_LIMITE ? `Últimas ${RECENTES_LIMITE} cotações` : "Cotações recentes"}
				</div>
				{recentes.length === 0 && (
					<div style={{ color: "#6B6B6B", fontSize: "0.85rem" }}>
						Nenhuma cotação ainda.
					</div>
				)}
				{recentes.map((q) => (
					<div
						key={q.id}
						onClick={() => goToDetail(q.id)}
						style={{
							cursor: "pointer",
							border: "1px solid #e3e3e3",
							borderRadius: "8px",
							padding: "0.5rem 0.75rem",
							marginBottom: "0.4rem",
							background: q.id === activeQuoteId ? "#F2F4F7" : "#fff"
						}}
					>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
							<div style={{ textAlign: "left" }}>
								<div style={{ fontWeight: 600 }}>
									{q.nome}{" "}
									{q.id === activeQuoteId && (
										<span style={{ color: "#814DE5", fontSize: "0.7rem" }}>● atual</span>
									)}
								</div>
								<div style={{ fontSize: "0.75rem", color: "#6B6B6B" }}>
									{(q.itens || []).length} propriedade(s)
								</div>
							</div>
							<Tag value={quoteAnyStatusLabel(q.status)} severity={quoteAnyStatusSeverity(q.status)} />
						</div>
					</div>
				))}
				<div style={{ marginTop: "0.5rem", textAlign: "center" }}>
					<Button
						label="Ver todas as cotações"
						text
						size="small"
						icon="pi pi-list"
						onClick={() => {
							op.current?.hide();
							navigate("/admin/cotacoes");
						}}
					/>
				</div>
			</div>
			</OverlayPanel>
			<QuoteDetailModal />
		</>
	);
};

const mapStateToProps = (store) => ({
	loginStatus: store.appReducer.loginStatus,
	quotes: store.quoteReducer.quotes,
	activeQuoteId: store.quoteReducer.activeQuoteId
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...QuoteActions }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(QuoteHeaderShortcut);
